import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { DateTimeUtility, EnumToKeyValueArray, isMobileView } from '@core/utils';
import { DropDownItem } from '@core/types/Dropdown.type';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DialogRef } from '@ngneat/dialog';
import { NgSelectModule } from '@ng-select/ng-select';
import { NgxBottomSheetService } from 'ngx-bottom-sheet';
import { RecurrenceOption, RecurrenceSchedule } from '@core/enums';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { NumberInputDirective } from '@shared/directives';
import { ButtonComponent, DropdownComponent, IconComponent } from '@shared/components/ui';
import { FormErrorComponent } from '@shared/components';

@UntilDestroy()
@Component({
  selector: 'app-dates-form',
  imports: [CommonModule, DropdownComponent, ReactiveFormsModule, TranslateModule, ButtonComponent, NgSelectModule, IconComponent, FormsModule, FormErrorComponent, NumberInputDirective],
  templateUrl: './recurrence-dates-form.component.html',
  styleUrls: ['./recurrence-dates-form.component.scss'],
})
export class RecurrenceDatesFormComponent implements OnInit {
  form: FormGroup;
  recurrenceOptions: any[];
  daysOfWeek: DropDownItem[] = [];
  protected readonly RecurrenceSchedule = RecurrenceSchedule;
  protected readonly RecurrenceOption = RecurrenceOption;
  protected readonly isMobileView = isMobileView;
  private readonly _fb = inject(FormBuilder);
  private readonly _translateService = inject(TranslateService);
  private readonly _bottomSheetService = inject(NgxBottomSheetService);
  private readonly _dialogRef: DialogRef<any>;

  constructor() {
    this._dialogRef = !isMobileView() ? inject(DialogRef) : null;
    this.recurrenceOptions = EnumToKeyValueArray(RecurrenceOption, this._translateService);
    this.daysOfWeek = DateTimeUtility.getWeekDays(this._translateService);
    this.form = this._fb.group({
      recurrence: ['', Validators.required],
      repeatEvery: ['', Validators.required],
      weekDays: [[]],
      dateStart: [null, Validators.required],
    });
  }

  ngOnInit(): void {
    const dateStart = isMobileView() ? this._bottomSheetService.currentSheetData.dateStart : this._dialogRef.data.dateStart;
    const recurrenceString = isMobileView() ? this._bottomSheetService.currentSheetData.currentRecurrence : this._dialogRef.data.currentRecurrence;
    this.form.get('dateStart').setValue(dateStart, { emitEvent: false });

    if (recurrenceString) {
      this._patchFormValues(recurrenceString);
    }

    this.form
      .get('recurrence')
      .valueChanges.pipe(untilDestroyed(this))
      .subscribe({
        next: (value) => {
          const weekDaysControl = this.form.get('weekDays');
          if (value === RecurrenceOption.RepeatWeekly) {
            weekDaysControl.setValidators(Validators.required);
          } else {
            weekDaysControl.clearValidators();
          }
          weekDaysControl.updateValueAndValidity();
        },
      });
  }

  close(): void {
    if (!isMobileView() && this._dialogRef) {
      this._dialogRef.close();
    } else {
      this._bottomSheetService.close();
    }
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const values = this.form.value;
    const rrule = this._generateRRule(values);

    if (!rrule || rrule.trim() === '') {
      this.form.markAllAsTouched();
      return;
    }

    if (isMobileView()) {
      this._bottomSheetService.close(rrule);
    } else {
      this._dialogRef.close(rrule);
    }
  }

  private _patchFormValues(recurrenceString: string): void {
    // Updated Regex to include BYMONTHDAY
    const rruleRegex = /FREQ=(\w+);INTERVAL=(\d+);(?:BYDAY=([a-zA-Z,]+);)?(?:BYMONTHDAY=(\d+);)?DTSTART=(\d+T\d+Z);(?:UNTIL=(\d+T\d+Z))?/;
    const matches = recurrenceString.match(rruleRegex);

    if (matches) {
      const [, freq, interval, byDay, byMonthDay, dtStart] = matches;
      const recurrence = this._mapFreqToRecurrenceOption(freq);
      const repeatEvery = parseInt(interval, 10);
      const weekDays = byDay ? this._mapDaysToWeekdayIndices(byDay.split(',')) : [];
      const dayOfMonth = byMonthDay ? parseInt(byMonthDay, 10) : null;
      const startDate = new Date(dtStart.slice(0, 4) + '-' + dtStart.slice(4, 6) + '-' + dtStart.slice(6, 8));

      // Patch the form values, including dayOfMonth if applicable
      this.form.patchValue({
        recurrence: recurrence,
        repeatEvery: repeatEvery,
        weekDays: weekDays,
        dateStart: startDate.toISOString().slice(0, 10), // YYYY-MM-DD format
      });

      if (dayOfMonth) {
        this.form.patchValue({ dayOfMonth: dayOfMonth });
      }

      // This is necessary to trigger any visibility toggles or validations dependent on recurrence type
      this.form.get('recurrence').updateValueAndValidity();
      this.form.get('weekDays').updateValueAndValidity();
    }
  }

  private _mapFreqToRecurrenceOption(freq: string): RecurrenceOption {
    switch (freq) {
      case 'DAILY':
        return RecurrenceOption.RepeatDaily;
      case 'WEEKLY':
        return RecurrenceOption.RepeatWeekly;
      case 'MONTHLY':
        return RecurrenceOption.RepeatMonthly;
      case 'YEARLY':
        return RecurrenceOption.RepeatYearly;
      default:
        return null; // Consider a default or error handling
    }
  }

  private _mapDaysToWeekdayIndices(days: string[]): number[] {
    const dayMap = { SU: 0, MO: 1, TU: 2, WE: 3, TH: 4, FR: 5, SA: 6 };
    return days.map((day) => dayMap[day]);
  }

  private _generateRRule(values: any): string {
    const { recurrence, repeatEvery, weekDays, dateStart } = values;
    let rruleString = '';
    const dtStartFormatted = DateTimeUtility.formatDateToUTCString(dateStart);

    switch (recurrence) {
      case RecurrenceOption.RepeatDaily:
        rruleString = `RRULE:FREQ=DAILY;INTERVAL=${repeatEvery};DTSTART=${dtStartFormatted}`;
        break;
      case RecurrenceOption.RepeatWeekly:
        if (weekDays.length) {
          const weekdayMap = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];
          const formattedDays = weekDays.map((day: any) => weekdayMap[day]).join(',');
          rruleString = `RRULE:FREQ=WEEKLY;INTERVAL=${repeatEvery};BYDAY=${formattedDays};DTSTART=${dtStartFormatted}`;
        }
        break;
      case RecurrenceOption.RepeatMonthly: {
        const dayOfMonth = new Date(dateStart).getUTCDate();
        rruleString = `RRULE:FREQ=MONTHLY;INTERVAL=${repeatEvery};BYMONTHDAY=${dayOfMonth};DTSTART=${dtStartFormatted}`;
        break;
      }
      case RecurrenceOption.RepeatYearly:
        rruleString = `RRULE:FREQ=YEARLY;INTERVAL=${repeatEvery};DTSTART=${dtStartFormatted}`;
        break;
      default:
        rruleString = '';
    }

    return rruleString;
  }
}
